<script setup lang="ts">
import {computed, ref} from "vue";
import {ChevronLeft, ChevronRight} from "@lucide/vue";
import {Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious} from "@/core/components/ui/carousel";
import {AlertLevel, AlertLine, AlertSource, AlertSourceStatus} from "@/medication-checkup/interfaces/alerts";
import MedicationCheckupAlertLevelCard from "@/medication-checkup/components/MedicationCheckupAlertLevelCard.vue";

/**
 * The row of one source of the analysis: its identity, its count and how it
 * ended, then its cards — one per level, the most serious first — in a
 * carousel dragged, wheeled or stepped with the arrows.
 *
 * A source that could not answer has no card: its status and its message
 * say why, in its own words.
 */

const props = defineProps<{
    source: AlertSource;
    lines: AlertLine[];
}>();

const emit = defineEmits<{open: [level: AlertLevel]}>();

const STATUS_CHIPS: Record<AlertSourceStatus, string> = {
    completed: 'bg-secondary-lightest text-secondary-darkest',
    skipped: 'bg-gray-light text-muted-foreground',
    unavailable: 'bg-orange/30 text-gray-dark',
    failed: 'bg-red-light/15 text-red-light'
};

/**
 * The pause between two wheel steps, so a single notch moves one card.
 */
const WHEEL_MS = 350;

/**
 * The arrows sit inside the rail and vanish at the end they cannot pass.
 */
const ARROW = 'shadow-custom text-primary border-slate-200 disabled:opacity-0 disabled:pointer-events-none';

const carousel = ref<InstanceType<typeof Carousel> | null>(null);

const hasCards = computed(() => props.source.levels.length > 0);

let wheelBlockedUntil = 0;

/**
 * Turn a wheel notch into one step of the carousel while it has room in that
 * direction, and let the page scroll otherwise.
 */
const wheel = (event: WheelEvent) => {
    const api = carousel.value?.carouselApi;
    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;

    if (!api || delta === 0) {
        return;
    }

    const forward = delta > 0;

    if (forward ? !api.canScrollNext() : !api.canScrollPrev()) {
        return;
    }

    event.preventDefault();

    if (Date.now() < wheelBlockedUntil) {
        return;
    }

    wheelBlockedUntil = Date.now() + WHEEL_MS;

    if (forward) {
        api.scrollNext();
    } else {
        api.scrollPrev();
    }
};
</script>

<template>
  <div class="flex flex-col gap-2.5">
    <div class="flex items-center gap-2.5 ml-10 max-w-[680px]">
      <span class="w-7 h-7 shrink-0 rounded-lg flex items-center justify-center text-[13px] font-bold bg-primary-dark text-white">
        {{ source.label.charAt(0) }}
      </span>
      <div class="flex flex-col min-w-0">
        <span class="text-sm font-semibold">{{ source.label }}</span>
        <span v-if="source.description" class="text-xs text-muted-foreground truncate">{{ source.description }}</span>
      </div>
      <span class="flex-1" />
      <span v-if="source.status === 'completed'" class="rounded-full px-2 text-xs font-semibold bg-gray-light text-gray-dark">
        {{ source.alerts_count }}
      </span>
      <span class="rounded-full px-2 text-xs font-semibold" :class="STATUS_CHIPS[source.status]">
        {{ source.status_label }}
      </span>
    </div>

    <p v-if="source.message" class="ml-10 max-w-[680px] text-xs text-muted-foreground">
      {{ source.message }}
    </p>

    <Carousel
      v-if="hasCards"
      ref="carousel"
      v-slot="{canScrollNext}"
      class="ml-10"
      :opts="{align: 'start', containScroll: 'trimSnaps'}"
      @wheel="wheel"
    >
      <CarouselContent class="ml-0 p-1.5">
        <CarouselItem
          v-for="level in source.levels"
          :key="level.severity"
          class="pl-0 pr-3 basis-auto"
        >
          <MedicationCheckupAlertLevelCard
            :source="source"
            :level="level"
            :lines="lines"
            @open="emit('open', level)"
          />
        </CarouselItem>
      </CarouselContent>

      <div
        v-if="canScrollNext"
        class="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-r from-transparent to-primary-lightest"
      />

      <template v-if="source.levels.length > 1">
        <CarouselPrevious :class="[ARROW, 'left-4']">
          <ChevronLeft class="w-4 h-4" />
          <span class="sr-only">Niveau précédent</span>
        </CarouselPrevious>
        <CarouselNext :class="[ARROW, 'right-4']">
          <ChevronRight class="w-4 h-4" />
          <span class="sr-only">Niveau suivant</span>
        </CarouselNext>
      </template>
    </Carousel>
  </div>
</template>
